import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function useCustomers() {
  const { authAxios } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("total_spent");
  const [sortDir, setSortDir] = useState("desc");
  const [sourceFilter, setSourceFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchStats = useCallback(async () => {
    try {
      const r = await authAxiosRef.current.get("/customers/stats");
      setStats(r.data);
    } catch {}
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        search: debouncedSearch,
        sort_by: sortBy,
        sort_dir: sortDir,
        page: String(page),
        limit: "50",
      });
      if (sourceFilter) params.set("source", sourceFilter);
      const res = await authAxiosRef.current.get(`/customers?${params.toString()}`);
      setCustomers(res.data.customers || []);
      setTotalPages(res.data.pages || 1);
      setTotalCount(res.data.total || 0);
    } catch {
      toast.error("Error cargando clientes");
    }
    setLoading(false);
  }, [debouncedSearch, sortBy, sortDir, sourceFilter, page]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { setLoading(true); fetchCustomers(); }, [fetchCustomers]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(1);
  };

  const handleSourceFilter = (value) => {
    setSourceFilter(value === "all" ? "" : value);
    setPage(1);
  };

  return {
    customers, stats, loading,
    search, setSearch,
    sortBy, sortDir, handleSort,
    sourceFilter, handleSourceFilter,
    page, setPage,
    totalPages, totalCount,
    fetchCustomers, fetchStats,
  };
}
